'use client';

import Link from '~/(tenant)/_components/tenant-link';
import Image from 'next/image';
import { useState } from 'react';

import type { LandingPageProps } from '../_lib/landing-page.types';

import './styles.css';

export default function TrsLandingPage(
  _props?: LandingPageProps,
) {
  const [aboutVisible, setAboutVisible] = useState(false);
  const [servicesVisible, setServicesVisible] = useState(false);

  return (
    <div className="trs-landing min-h-screen bg-[var(--trs-cream)] overflow-x-hidden">
      {/* Subtle grain texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-[100] opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden bg-[#2a3d2e]">
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src="/landing-pages/trs/images/hero-court.webp"
            alt="The Racquet Space"
            fill
            className="object-cover object-center scale-105"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/20" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-12 w-full pt-32 pb-20">
          <div className="max-w-2xl">
            <div className="overflow-hidden mb-6">
              <p
                className="text-white text-lg font-bold tracking-[0.3em] uppercase trs-animate-slide-up"
                style={{
                  animationDelay: '0.2s',
                  animationFillMode: 'both',
                  textShadow:
                    '0 2px 12px rgba(0,0,0,0.8), 0 4px 20px rgba(0,0,0,0.6)',
                }}
              >
                EST. 2023 — JEDDAH, SAUDI ARABIA
              </p>
            </div>

            <div className="space-y-2 mb-10">
              {[
                'Exclusive benefits,',
                'unforgettable experiences,',
                'and endless opportunities',
              ].map((line, i) => (
                <div key={i} className="overflow-hidden">
                  <h1
                    className="text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold leading-[1.1] trs-animate-slide-up font-myriad-semibold"
                    style={{
                      animationDelay: `${0.3 + i * 0.1}s`,
                      animationFillMode: 'both',
                    }}
                  >
                    {line}
                  </h1>
                </div>
              ))}
            </div>

            <div
              className="trs-animate-fade-in flex flex-col sm:flex-row items-start sm:items-center gap-4"
              style={{ animationDelay: '0.8s', animationFillMode: 'both' }}
            >
              <Link
                href="/book"
                className="group inline-flex items-center gap-4 bg-[var(--trs-orange)] text-white px-10 py-4 rounded-full text-lg tracking-[0.2em] uppercase hover:bg-[#D57E50] transition-all duration-300 hover:gap-6"
              >
                <span>Book Now</span>
                <svg
                  className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>

              <Link
                href="/memberships"
                className="inline-flex items-center gap-3 border border-white/60 text-white px-10 py-4 rounded-full text-lg tracking-[0.2em] uppercase hover:bg-white hover:text-[var(--trs-green)] transition-all duration-300"
              >
                <span>Memberships</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        ref={(node) => {
          if (!node || aboutVisible) return;
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry?.isIntersecting) {
                setAboutVisible(true);
                observer.disconnect();
              }
            },
            { threshold: 0.2 },
          );
          observer.observe(node);
        }}
        className="relative bg-[var(--trs-beige)] overflow-hidden"
      >
        <div className="flex flex-col lg:flex-row min-h-screen">
          {/* Left - Image */}
          <div className="w-full lg:w-1/2 relative min-h-[60vh] lg:min-h-screen">
            <Image
              src="/landing-pages/trs/images/about-section.webp"
              alt="Tennis player at The Racquet Space"
              fill
              className={`object-cover transition-transform duration-1000 ${
                aboutVisible ? 'scale-100' : 'scale-110'
              }`}
            />
            <div
              className={`absolute inset-8 border border-white/30 pointer-events-none transition-opacity duration-1000 delay-500 ${
                aboutVisible ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div
              className={`absolute bottom-12 left-12 transition-all duration-700 delay-700 ${
                aboutVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="bg-[var(--trs-green)] text-white px-6 py-4">
                <p className="text-lg tracking-[0.3em] mb-1">ESTABLISHED</p>
                <p className="text-4xl font-semibold font-myriad-semibold">
                  2023
                </p>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="w-full lg:w-1/2 flex items-center relative">
            <div
              className={`hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 w-24 h-[1px] bg-[var(--trs-green)]/30 transition-all duration-1000 ${
                aboutVisible
                  ? 'opacity-100 w-24'
                  : 'opacity-0 w-0'
              }`}
            />

            <div className="px-8 lg:px-16 xl:px-24 py-20 lg:py-32 max-w-2xl mx-auto lg:mx-0">
              <div
                className={`mb-12 transition-all duration-700 ${
                  aboutVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
              >
                <span className="text-[var(--trs-orange)] text-lg tracking-[0.3em] uppercase">
                  About Us
                </span>
                <h2 className="text-[var(--trs-green)] text-4xl lg:text-5xl mt-4 leading-tight font-semibold font-myriad-semibold">
                  Where Passion Meets Excellence
                </h2>
              </div>

              {[
                {
                  bold: 'The Racquet Space,',
                  text: "where we blend premier racquet sports with luxury living in Jeddah. Where enthusiasts find more than just a sports facility; it's a community for tennis.",
                },
                {
                  bold: 'Our goal',
                  text: 'is to provide a well-rounded experience that supports your growth and enjoyment in the world of tennis and racquet sports.',
                },
                {
                  bold: 'Come join',
                  text: 'The Racquet Space and experience our unique blend of vintage charm and modern facilities. Enjoy our exclusive benefits, unforgettable experiences, and endless opportunities.',
                },
              ].map((para, i) => (
                <p
                  key={i}
                  className={`text-[var(--trs-green)] text-xl lg:text-2xl leading-relaxed mb-8 transition-all duration-700 ${
                    aboutVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${300 + i * 150}ms` }}
                >
                  <span className="font-semibold">{para.bold}</span>{' '}
                  {para.text}
                </p>
              ))}

              <div
                className={`flex gap-12 mt-12 pt-8 border-t border-[var(--trs-green)]/20 transition-all duration-700 delay-700 ${
                  aboutVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                }`}
              >
                {[
                  { number: '2', label: 'Clay Courts' },
                  { number: '1', label: 'Hard Court' },
                  { number: '1', label: 'Fitness Studio' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <p className="text-[var(--trs-orange)] text-4xl lg:text-5xl font-semibold font-myriad-semibold">
                      {stat.number}
                    </p>
                    <p className="text-[var(--trs-green)]/60 text-lg tracking-[0.1em] mt-1">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        ref={(node) => {
          if (!node || servicesVisible) return;
          const observer = new IntersectionObserver(
            ([entry]) => {
              if (entry?.isIntersecting) {
                setServicesVisible(true);
                observer.disconnect();
              }
            },
            { threshold: 0.1 },
          );
          observer.observe(node);
        }}
        className="bg-white py-20 lg:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 mb-16">
          <div
            className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 transition-all duration-700 ${
              servicesVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8'
            }`}
          >
            <div>
              <span className="text-[var(--trs-orange)] text-lg tracking-[0.3em] uppercase">
                Our Facilities
              </span>
              <h2 className="text-[var(--trs-green)] text-4xl lg:text-5xl mt-4 font-semibold font-myriad-semibold">
                World-Class Amenities
              </h2>
            </div>
            <p className="text-[var(--trs-green)]/60 max-w-md text-xl">
              Every detail designed to elevate your game and enhance your
              experience.
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Tennis - Large Card */}
            <div
              className={`lg:col-span-7 group relative overflow-hidden rounded-2xl min-h-[500px] transition-all duration-700 ${
                servicesVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: '200ms' }}
            >
              <Image
                src="/landing-pages/trs/images/padel-court.webp"
                alt="Tennis Court"
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--trs-green)]/90 via-[var(--trs-green)]/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
                <h3 className="text-white text-3xl lg:text-4xl mt-2 mb-4 font-semibold font-myriad-semibold">
                  Tennis
                </h3>
                <p className="text-white/80 text-lg max-w-md leading-relaxed">
                  <span className="text-white font-medium">
                    Explore tennis beyond the game
                  </span>{' '}
                  &mdash; a journey of skill and shared passion. Our club boasts
                  two clay courts and one hard court, offering the perfect
                  surface for every style of play.
                </p>
              </div>
            </div>

            {/* Right Column - Stacked Cards */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              {/* Gym Card */}
              <div
                className={`group relative overflow-hidden rounded-2xl min-h-[240px] flex-1 transition-all duration-700 ${
                  servicesVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: '400ms' }}
              >
                <Image
                  src="/landing-pages/trs/images/gym.webp"
                  alt="Gym"
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--trs-green)]/90 via-[var(--trs-green)]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                  <h3 className="text-white text-2xl lg:text-3xl mt-1 mb-2 font-semibold font-myriad-semibold">
                    Conditioning Studio
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed">
                    <span className="text-white font-medium">Stay active</span>{' '}
                    and enhance your performance with our well-equipped gym.
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 mt-16">
          <div
            className={`bg-[var(--trs-green)] rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6 transition-all duration-700 delay-700 ${
              servicesVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-12'
            }`}
          >
            <div>
              <h3 className="text-white text-2xl lg:text-3xl mb-2 font-semibold font-myriad-semibold">
                Ready to elevate your game?
              </h3>
              <p className="text-white/70 text-lg">
                Book your court today and become part of our community.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0">
              <Link
                href="/book"
                className="bg-[var(--trs-orange)] text-white px-8 py-4 rounded-full text-lg tracking-[0.15em] uppercase hover:bg-[#D57E50] transition-colors text-center"
              >
                Book Now
              </Link>
              <Link
                href="/memberships"
                className="border border-white/60 text-white px-8 py-4 rounded-full text-lg tracking-[0.15em] uppercase hover:bg-white hover:text-[var(--trs-green)] transition-colors text-center"
              >
                Memberships
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[var(--trs-beige)] py-12 lg:py-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="text-center mb-8">
            <span className="text-[var(--trs-orange)] text-lg tracking-[0.3em] uppercase">
              Visit Us
            </span>
            <h2 className="text-[var(--trs-green)] text-4xl lg:text-5xl mt-4 font-semibold font-myriad-semibold">
              Get in Touch
            </h2>
          </div>

          <div className="bg-[var(--trs-green)] rounded-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left - Contact Details */}
              <div className="p-6 lg:p-8 xl:p-10">
                <div className="space-y-6">
                  <ContactItem
                    icon={<LocationIcon />}
                    title="Location"
                  >
                    <p className="text-white/60 text-lg leading-relaxed">
                      6465 Abi Is&apos;haq Alnahawi,
                      <br />
                      Jeddah 23734, Saudi Arabia
                    </p>
                  </ContactItem>

                  <ContactItem icon={<ClockIcon />} title="Opening Hours">
                    <div className="text-white/60 text-lg space-y-0.5">
                      <p>Friday: 3 PM &ndash; 11 PM</p>
                      <p>Saturday &ndash; Sunday: 8 AM &ndash; 11 PM</p>
                      <p>Monday: 8 PM &ndash; 11 PM</p>
                      <p>Tuesday: 8 AM &ndash; 5 PM</p>
                      <p>Wednesday &ndash; Thursday: 8 AM &ndash; 11 PM</p>
                    </div>
                  </ContactItem>

                  <ContactItem icon={<PhoneIcon />} title="Contact">
                    <div className="space-y-1">
                      <a
                        href="tel:+966555945217"
                        className="block text-white/60 text-lg hover:text-[var(--trs-orange)] transition-colors"
                      >
                        +966 55 594 5217
                      </a>
                      <a
                        href="mailto:cs@theracquetspace.com"
                        className="block text-white/60 text-lg hover:text-[var(--trs-orange)] transition-colors"
                      >
                        cs@theracquetspace.com
                      </a>
                    </div>
                  </ContactItem>
                </div>

                {/* Actions & Social Links */}
                <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-white/10">
                  <a
                    href="https://maps.app.goo.gl/U6zd4HjK2ztU6qcq5"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--trs-orange)] text-white text-lg rounded-full hover:bg-[#D57E50] transition-colors"
                  >
                    <LocationIcon />
                    <span>Get Directions</span>
                  </a>
                  <a
                    href="https://www.instagram.com/theracquetspace"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white hover:border-white/40 transition-all"
                    aria-label="Instagram"
                  >
                    <InstagramIcon />
                  </a>
                  <a
                    href="https://wa.me/966555945217"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:bg-white/10 hover:text-white hover:border-white/40 transition-all"
                    aria-label="WhatsApp"
                  >
                    <WhatsAppIcon />
                  </a>
                </div>
              </div>

              {/* Right - Google Maps */}
              <div className="h-[350px] lg:h-auto lg:min-h-[450px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1863.2!2d39.1315996!3d21.7500659!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15c1632ef6beef5b%3A0xb346d92e40cb0543!2sThe%20Racquet%20Space!5e0!3m2!1sen!2sae!4v1767369320180!5m2!1sen!2sae"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="The Racquet Space Location"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactItem({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-white text-lg font-medium mb-1">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function LocationIcon() {
  return (
    <svg
      className="w-4 h-4 text-[var(--trs-orange)]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      className="w-4 h-4 text-[var(--trs-orange)]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      className="w-4 h-4 text-[var(--trs-orange)]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
